import {
  ProjectsSearchQuery,
  ProjectsSearchQueryVariables,
  VendorProjectsSearchQuery,
  VendorProjectsSearchQueryVariables,
} from "@/__generated__/graphql";
import { useUserContext } from "@/contexts/UserContextProvider";
import {
  PROJECTS_SEARCH_QUERY,
  VENDOR_PROJECTS_SEARCH_QUERY,
} from "@/graphql/queries/ProjectSearchQuery";
import { mapNodes } from "@/helpers/utils";
import { useQuery } from "@apollo/client";
import { debounce } from "lodash";
import { useState, useCallback, useEffect } from "react";

const PROJECT_SEARCH_PAGINATION_LIMIT = 20;
const PROJECT_SEARCH_DEBOUNCE_DURATION = 400;

export function useProjectSearch() {
  const { currentUser } = useUserContext();

  const clientProjectSearchResults = useClientProjectSearch(
    !!currentUser?.canBeAssignedTasks,
  );
  const vendorProjectSearchResults = useVendorProjectSearch(
    !currentUser?.canBeAssignedTasks,
  );

  return currentUser?.canBeAssignedTasks
    ? { ...vendorProjectSearchResults }
    : { ...clientProjectSearchResults };
}

export function useClientProjectSearch(
  skip = false,
  first = PROJECT_SEARCH_PAGINATION_LIMIT,
  searchProjectsDebounceMs = PROJECT_SEARCH_DEBOUNCE_DURATION,
) {
  const { currentProject } = useUserContext();

  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSetSearchTerm = useCallback(
    debounce(setSearchTerm, searchProjectsDebounceMs),
    [],
  );

  const commonVariables = {
    selectedProjectId: currentProject?.id ?? undefined,
    first,
  };

  useEffect(() => {
    return () => {
      debouncedSetSearchTerm.cancel();
    };
  }, [debouncedSetSearchTerm]);

  const { loading, data, fetchMore } = useQuery<
    ProjectsSearchQuery,
    ProjectsSearchQueryVariables
  >(PROJECTS_SEARCH_QUERY, {
    fetchPolicy: "no-cache",
    variables: {
      ...commonVariables,
      ...(searchTerm ? { projectSearchTerm: searchTerm } : {}),
    },
    skip,
  });

  const projects = mapNodes(data?.recentProjects);
  const { endCursor, hasNextPage = false } =
    data?.recentProjects?.pageInfo ?? {};

  const loadMore = async () => {
    await fetchMore({
      variables: {
        selectedProjectId: currentProject?.id,
        first,
        after: endCursor,
      },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        if (!fetchMoreResult) return previousResult;

        const pResult = previousResult;
        const nResult = fetchMoreResult;

        return {
          ...nResult,
          recentProjects: {
            ...nResult.recentProjects,
            edges: [
              ...(pResult.recentProjects?.edges || []),
              ...(nResult.recentProjects?.edges || []),
            ],
          },
        } as ProjectsSearchQuery;
      },
    });
  };

  return {
    hasNextPage,
    loadMore,
    loading,
    searchProject: debouncedSetSearchTerm,
    projects,
  };
}

export function useVendorProjectSearch(
  skip = false,
  limit = PROJECT_SEARCH_PAGINATION_LIMIT,
  searchProjectsDebounceMs = PROJECT_SEARCH_DEBOUNCE_DURATION,
) {
  const { currentProject } = useUserContext();

  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSetSearchTerm = useCallback(
    debounce(setSearchTerm, searchProjectsDebounceMs),
    [],
  );

  const commonVariables: VendorProjectsSearchQueryVariables = {
    selectedProjectId: currentProject?.id,
    limit,
  };

  useEffect(() => {
    return () => {
      debouncedSetSearchTerm.cancel();
    };
  }, [debouncedSetSearchTerm]);

  const { loading, data, fetchMore } = useQuery<
    VendorProjectsSearchQuery,
    VendorProjectsSearchQueryVariables
  >(VENDOR_PROJECTS_SEARCH_QUERY, {
    fetchPolicy: "no-cache",
    variables: {
      ...commonVariables,
      ...(searchTerm ? { name: searchTerm } : {}),
    },
    skip,
  });

  const projects = mapNodes(data?.vendorProjects);
  const { endCursor, hasNextPage = false } =
    data?.vendorProjects?.pageInfo ?? {};

  const loadMore = async () => {
    await fetchMore({
      variables: {
        ...commonVariables,
        after: endCursor,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;
        return {
          ...fetchMoreResult,
          vendorProjects: {
            ...fetchMoreResult.vendorProjects,
            edges: [
              ...(prev.vendorProjects?.edges || []),
              ...(fetchMoreResult.vendorProjects?.edges || []),
            ],
          },
        } as VendorProjectsSearchQuery;
      },
    });
  };

  return {
    hasNextPage,
    loadMore,
    loading,
    searchProject: debouncedSetSearchTerm,
    projects,
  };
}
